import { ActionReducerMapBuilder, createAsyncThunk } from '@reduxjs/toolkit';
import { Position, WidgetType } from '@kemu-io/kemu-core/types';
import { LogicMapperState } from '../logicMapperSlice';
import { initializeAddedWidgets } from './common/widgetInitialization';
import * as RecipeActions from '@src/app/recipe/utils';
import { safeJsonClone } from '@common/utils';
import { RootState } from '@src/app/store';

type AddWidgetToRecipeProps = {
  recipeId: string;
  blockRecipeId: string;
  parentGroupId?: string;
  gateType: WidgetType;
  position: Position;
}

/**
 * Adds a widget to the recipe and initializes it properly.
 * This replaces the old addGate action with proper initialization.
 */
const addWidgetToRecipeAction = createAsyncThunk(
  '/LogicMapper/addWidgetToRecipe', async (
    payload: AddWidgetToRecipeProps,
    thunkApi
  ) => {
    // Add to the recipe
    const addedWidget = RecipeActions.addWidgetToThing({
      recipeId: payload.recipeId,
      thingId: payload.blockRecipeId,
      type: payload.gateType,
      position: payload.position,
      parentGroupId: payload.parentGroupId
    });

    // Initialize the widget
    const { user } = thunkApi.getState() as RootState;
    await initializeAddedWidgets({
      recipeId: payload.recipeId,
      thingId: payload.blockRecipeId,
      addedWidgetsList: [addedWidget],
      thunkApi,
      userId: user.currentUserProfile.profile?.id
    });

    // Make sure the state is not included in the store
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { state: _, ...statelessWidget } = addedWidget;

    return {
      widget: safeJsonClone(statelessWidget),
      widgetId: addedWidget.id
    };
  }
);

const addWidgetReducer = ((builder: ActionReducerMapBuilder<LogicMapperState>): void => {
  builder.addCase(addWidgetToRecipeAction.fulfilled, (state, action) => {
    // Add to global state
    // NOTE: Avoid passing the original objects since immer will freeze them and
    // prevent us from updating properties in the future, such as widget position, etc
    state.gates[action.payload.widgetId] = action.payload.widget;
    state.lastAddedWidgetId = action.payload.widgetId;
  });

  builder.addCase(addWidgetToRecipeAction.rejected, (_state, action) => {
    console.error('Failed to add widget to recipe: ', action.error);
  });
});

export default {
  action: addWidgetToRecipeAction,
  reducer: addWidgetReducer,
};
